/**
 * Persistent per-chat notification watermarks.
 *
 * A watermark records the `lastMessageAt` timestamp (ms) of the newest chat
 * update this browser has already handled (notified for, or suppressed because
 * the user was looking at the chat). Notification delivery is level-triggered —
 * "row is unread AND newer than its watermark" — so persisting the watermark in
 * localStorage is what makes delivery at-most-once across polls, remounts,
 * reloads, and same-origin tabs.
 */
import { browserLogger } from '@newfront-insurance/dd-rum';

const STORAGE_KEY = 'nova:notified-watermarks-v1';

/** Bound the map so one busy user can't grow localStorage forever. */
const MAX_ENTRIES = 200;

type WatermarkMap = Record<string, number>;

/** Fallback when localStorage is unavailable (private mode, quota, SSR). */
let memoryFallback: WatermarkMap | null = null;

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return window.localStorage;
  } catch {
    return null;
  }
}

function readAll(): WatermarkMap {
  const storage = getStorage();
  if (!storage) {
    memoryFallback ??= {};
    return memoryFallback;
  }

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => typeof value === 'number' && Number.isFinite(value)),
    ) as WatermarkMap;
  } catch (error) {
    browserLogger.debug('[Nova Notifications] Failed to read watermarks', { error });
    return {};
  }
}

function writeAll(map: WatermarkMap): void {
  const entries = Object.entries(map);
  const pruned =
    entries.length > MAX_ENTRIES
      ? Object.fromEntries(entries.sort(([, a], [, b]) => b - a).slice(0, MAX_ENTRIES))
      : map;

  const storage = getStorage();
  if (!storage) {
    memoryFallback = pruned;
    return;
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(pruned));
  } catch (error) {
    memoryFallback = pruned;
    browserLogger.debug('[Nova Notifications] Failed to persist watermarks', { error });
  }
}

export function getNotifiedWatermark(chatUuid: string): number | undefined {
  return readAll()[chatUuid];
}

/**
 * Atomically (synchronous read-then-write) claims a watermark. Returns false
 * when an equal-or-newer watermark already exists — meaning another evaluator
 * run (same tab or sibling tab) already handled this update — and true after
 * recording it. Callers must claim BEFORE showing a notification.
 */
export function claimNotifiedWatermark(chatUuid: string, timestampMs: number): boolean {
  const map = readAll();
  const existing = map[chatUuid];
  if (existing !== undefined && existing >= timestampMs) {
    return false;
  }

  writeAll({ ...map, [chatUuid]: timestampMs });
  return true;
}

/**
 * Rolls a watermark back after a failed display so the level-triggered retry
 * is preserved: if `new Notification()` throws, the next emission may try
 * again instead of permanently consuming the chat's delivery.
 */
export function releaseNotifiedWatermark(chatUuid: string, timestampMs: number): void {
  const map = readAll();
  if (map[chatUuid] !== timestampMs) {
    return;
  }

  const rest = Object.fromEntries(Object.entries(map).filter(([key]) => key !== chatUuid));
  writeAll(rest);
}
